import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Select, Textarea, Label, KPI } from "@/components/Primitives";
import { StatusPill, leadStageVariant } from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import {
  ChevronLeft, Save, Trash2, Mail, Phone, Globe, MapPin, Navigation,
  Calendar, User, Building2, Flame, TrendingUp, FileText, UserPlus, ArrowRight, Briefcase,
} from "lucide-react";
import { toast } from "sonner";

const STAGES = ["New", "Qualified", "Proposal", "Won", "Lost"];
const INDUSTRIES = ["Sports", "Retail", "Finance", "Media", "Education", "SaaS", "Other"];
const SOURCES = ["Website", "Referral", "Outbound", "Event", "Partner", "Other"];

function gbp(v) {
  const n = v ?? 0;
  const hasPence = Math.round(n * 100) !== Math.round(n) * 100;
  return `£${n.toLocaleString(undefined, { minimumFractionDigits: hasPence ? 2 : 0, maximumFractionDigits: 2 })}`;
}

export default function LeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [team, setTeam] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [showWoModal, setShowWoModal] = useState(false);
  const [dirty, setDirty] = useState(false);
  const isOwner = user?.role === "admin";

  useEffect(() => {
    (async () => {
      try {
        const [l, t, c] = await Promise.all([
          api.get(`/leads/${id}`),
          api.get("/team"),
          api.get("/customers"),
        ]);
        setLead(l.data); setForm(l.data);
        setTeam(t.data || []);
        setCustomers(c.data || []);
      } catch {
        toast.error("Lead not found");
        nav("/leads");
      }
    })();
  }, [id, nav]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const setOwner = (memberId) => {
    if (!memberId) { set("owner_id", null); set("owner", "Unassigned"); return; }
    const m = team.find((x) => x.id === memberId);
    setForm((f) => ({ ...f, owner_id: memberId, owner: m?.name || "Unassigned" }));
    setDirty(true);
  };

  const save = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        est_value: parseFloat(form.est_value) || 0,
        win_likelihood: parseInt(form.win_likelihood) || 0,
        lat: form.lat ? parseFloat(form.lat) : null,
        lng: form.lng ? parseFloat(form.lng) : null,
      };
      const { data } = await api.patch(`/leads/${id}`, payload);
      setLead(data); setForm(data); setDirty(false);
      toast.success("Lead saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete lead "${lead.company}"? This removes it from Firestore too.`)) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      nav("/leads");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (!form) return <div className="text-sm text-muted">Loading…</div>;

  // Match lead → existing customer (case-insensitive on company name OR converted_customer_id pointer)
  const matchedCustomer = (() => {
    if (form.converted_customer_id) {
      return customers.find((c) => c.id === form.converted_customer_id);
    }
    return customers.find((c) => (c.company || "").trim().toLowerCase() === (form.company || "").trim().toLowerCase());
  })();

  const convertToCustomer = async () => {
    if (dirty) {
      const ok = window.confirm("You have unsaved lead changes. Save them first? (Cancel to convert without saving)");
      if (ok) await save();
    }
    if (!window.confirm(`Convert "${form.company}" to a customer? The lead will be marked as Won.`)) return;
    setConverting(true);
    try {
      const { data } = await api.post(`/leads/${id}/convert-to-customer`);
      toast.success(`${data.company} added as a customer`);
      nav(`/customers/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Conversion failed");
    } finally { setConverting(false); }
  };

  const mapQuery = encodeURIComponent(form.address || form.company);
  const mapSrc = (form.address || form.lat)
    ? `https://www.google.com/maps?q=${form.lat ? `${form.lat},${form.lng}` : mapQuery}&output=embed`
    : null;
  const directionsUrl = form.address
    ? `https://www.google.com/maps/dir/?api=1&destination=${mapQuery}`
    : null;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Sales · Leads"
        title={
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/leads")} className="p-1 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))]"><ChevronLeft className="w-5 h-5" strokeWidth={2}/></button>
            {lead?.company || "Lead"}
          </div>
        }
        subtitle={
          <div className="inline-flex items-center gap-2">
            <StatusPill variant={leadStageVariant(form.stage)}>{form.stage}</StatusPill>
            {matchedCustomer && (
              <button onClick={() => nav(`/customers/${matchedCustomer.id}`)} className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors" data-testid="lead-customer-link">
                <Building2 className="w-3 h-3" strokeWidth={2}/>Customer · {matchedCustomer.company} <ArrowRight className="w-3 h-3" strokeWidth={2}/>
              </button>
            )}
          </div>
        }
        actions={
          <div className="flex items-center gap-2">
            {isOwner && !matchedCustomer && (
              <Button variant="secondary" onClick={convertToCustomer} disabled={converting} data-testid="convert-to-customer-btn">
                <UserPlus className="w-4 h-4" strokeWidth={1.6}/>{converting ? "Converting…" : "Convert to customer"}
              </Button>
            )}
            {isOwner && matchedCustomer && (
              <Button variant="secondary" onClick={() => setShowWoModal(true)} data-testid="add-work-order-btn">
                <Briefcase className="w-4 h-4" strokeWidth={1.6}/>Add work order
              </Button>
            )}
            {isOwner && (
              <Button variant="ghost" onClick={onDelete} className="text-rose-600 hover:text-rose-700" data-testid="delete-lead-detail-btn">
                <Trash2 className="w-4 h-4" strokeWidth={1.6}/>Delete
              </Button>
            )}
            <Button onClick={save} disabled={!dirty || saving} data-testid="save-lead-detail-btn">
              <Save className="w-4 h-4" strokeWidth={2}/>{saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Est. value" value={gbp(form.est_value)} variant="primary" />
        <KPI label="Win likelihood" value={`${form.win_likelihood}%`} />
        <KPI label="Priority" value={form.priority || "Normal"} variant={form.priority === "High" ? "warning" : "default"} />
        <KPI label="Owner" value={form.owner || "Unassigned"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left. Main details form */}
        <form onSubmit={save} className="lg:col-span-2 space-y-4">
          <Card title="Company">
            <div className="p-5 grid grid-cols-2 gap-4">
              <div><Label>Company *</Label><Input required value={form.company || ""} onChange={(e) => set("company", e.target.value)}/></div>
              <div><Label>Industry</Label>
                <Select value={form.industry || ""} onChange={(e) => set("industry", e.target.value)}>
                  <option value="">—</option>
                  {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
                </Select>
              </div>
              <div className="col-span-2"><Label>Website</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.6}/>
                  <Input value={form.website || ""} onChange={(e) => set("website", e.target.value)} className="pl-9" placeholder="https://…"/>
                </div>
              </div>
            </div>
          </Card>

          <Card title="Contact">
            <div className="p-5 grid grid-cols-2 gap-4">
              <div><Label>Main contact *</Label><Input required value={form.contact || ""} onChange={(e) => set("contact", e.target.value)}/></div>
              <div><Label>Position</Label><Input value={form.position || ""} onChange={(e) => set("position", e.target.value)}/></div>
              <div><Label>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.6}/>
                  <Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="pl-9"/>
                </div>
              </div>
              <div><Label>Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.6}/>
                  <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className="pl-9"/>
                </div>
              </div>
            </div>
          </Card>

          <Card title="Pipeline">
            <div className="p-5 grid grid-cols-2 gap-4">
              <div><Label>Stage</Label>
                <Select value={form.stage} onChange={(e) => set("stage", e.target.value)} data-testid="detail-stage">
                  {STAGES.map((s) => <option key={s}>{s}</option>)}
                </Select>
              </div>
              <div><Label>Source</Label>
                <Select value={form.source || "Website"} onChange={(e) => set("source", e.target.value)}>
                  {SOURCES.map((s) => <option key={s}>{s}</option>)}
                </Select>
              </div>
              <div><Label>Priority</Label>
                <Select value={form.priority || "Normal"} onChange={(e) => set("priority", e.target.value)}>
                  <option>Low</option><option>Normal</option><option>High</option>
                </Select>
              </div>
              <div><Label>Owner (team member)</Label>
                <Select value={form.owner_id || ""} onChange={(e) => setOwner(e.target.value)} data-testid="detail-owner">
                  <option value="">Unassigned</option>
                  {team.filter((m) => m.active !== false).map((m) => (
                    <option key={m.id} value={m.id}>{m.name} {m.type === "external" ? "(external)" : ""}</option>
                  ))}
                </Select>
              </div>
              <div><Label>Est. value (ex VAT)</Label><Input type="number" value={form.est_value ?? 0} onChange={(e) => set("est_value", e.target.value)}/></div>
              <div>
                <div className="flex justify-between"><Label>Win likelihood</Label><span className="text-[11px] font-mono">{form.win_likelihood}%</span></div>
                <input type="range" min="0" max="100" step="5" value={form.win_likelihood ?? 50} onChange={(e) => set("win_likelihood", e.target.value)} className="w-full accent-[hsl(var(--primary))] mt-2"/>
              </div>
              <div className="col-span-2"><Label>Next action</Label><Input value={form.next_action || ""} onChange={(e) => set("next_action", e.target.value)} placeholder="Call / Send proposal / Schedule demo"/></div>
              <div className="col-span-2"><Label>Due date</Label><Input type="date" value={(form.due || "").split("T")[0]} onChange={(e) => set("due", e.target.value)}/></div>
            </div>
          </Card>

          <Card title="Notes">
            <div className="p-5">
              <Textarea rows={5} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Context, requirements, objections, next steps…"/>
            </div>
          </Card>
        </form>

        {/* Right. Map + location + meta */}
        <div className="space-y-4">
          <Card title="Location" right={directionsUrl && <a href={directionsUrl} target="_blank" rel="noreferrer" className="text-[12px] font-semibold text-[hsl(var(--primary))] inline-flex items-center gap-1"><Navigation className="w-3 h-3" strokeWidth={2}/>Directions</a>}>
            <div className="p-5 space-y-3">
              <div><Label>Address</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.6}/>
                  <Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} className="pl-9" placeholder="Street, City, Postcode" data-testid="lead-address"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Latitude</Label><Input type="number" step="any" value={form.lat ?? ""} onChange={(e) => set("lat", e.target.value)} placeholder="53.341"/></div>
                <div><Label>Longitude</Label><Input type="number" step="any" value={form.lng ?? ""} onChange={(e) => set("lng", e.target.value)} placeholder="-2.729"/></div>
              </div>
              {mapSrc ? (
                <div className="rounded-lg overflow-hidden border border-[hsl(var(--border))] h-[260px] bg-[hsl(var(--background))]">
                  <iframe
                    title="Location map"
                    width="100%"
                    height="260"
                    frameBorder="0"
                    src={mapSrc}
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    data-testid="lead-map-iframe"
                  />
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-[hsl(var(--border))] h-[180px] grid place-items-center text-[12px] text-[hsl(var(--text-muted))]">
                  <div className="text-center">
                    <MapPin className="w-5 h-5 mx-auto mb-2 opacity-50" strokeWidth={1.6}/>
                    Add an address above to show the map and plan travel
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card title="Meta">
            <div className="p-5 space-y-3 text-[12px]">
              <Row icon={User} label="Contact" value={`${form.contact || "—"}${form.position ? ` (${form.position})` : ""}`} />
              <Row icon={Building2} label="Industry" value={form.industry || "—"} />
              <Row icon={Flame} label="Priority" value={form.priority} />
              <Row icon={TrendingUp} label="Source" value={form.source} />
              <Row icon={Calendar} label="Created" value={new Date(form.created_at).toLocaleString()} />
              {form.firestore_lead_id && <Row icon={FileText} label="Firestore ID" value={form.firestore_lead_id} mono />}
            </div>
          </Card>
        </div>
      </div>
      {showWoModal && matchedCustomer && (
        <QuickWorkOrderModal
          customer={matchedCustomer}
          leadCompany={form.company}
          leadNotes={form.notes}
          team={team}
          onClose={() => setShowWoModal(false)}
          onCreated={() => { setShowWoModal(false); toast.success("Work order created"); }}
        />
      )}
    </div>
  );
}

function QuickWorkOrderModal({ customer, leadCompany, leadNotes, team, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: `Initial brief, ${leadCompany}`,
    description: leadNotes || "",
    priority: "Normal",
    status: "pending",
    assignee_id: "",
    assignee: "",
    due_date: "",
  });
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects((data || []).filter((p) => p.customer_id === customer.id))).catch(() => {});
  }, [customer.id]);

  const setAssignee = (mid) => {
    if (!mid) { setForm((f) => ({ ...f, assignee_id: "", assignee: "" })); return; }
    const m = team.find((x) => x.id === mid);
    setForm((f) => ({ ...f, assignee_id: mid, assignee: m?.name || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      await api.post("/work-orders", {
        title: form.title,
        description: form.description,
        customer_id: customer.id,
        customer_name: customer.company,
        project_id: projectId || null,
        priority: form.priority,
        status: form.status,
        assignee: form.assignee || null,
        assignee_id: form.assignee_id || null,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
      });
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create work order");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-xl rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl" data-testid="quick-wo-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <h3 className="text-lg font-semibold">New work order · {customer.company}</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><Trash2 className="w-4 h-4 rotate-45 opacity-0"/><span className="text-xl leading-none">×</span></button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label>Title *</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}/></div>
          <div className="md:col-span-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What needs doing? Include scope, deliverables, deadlines."/></div>
          <div><Label>Priority</Label>
            <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              <option>Low</option><option>Normal</option><option>High</option><option>Urgent</option>
            </Select>
          </div>
          <div><Label>Assignee</Label>
            <Select value={form.assignee_id} onChange={(e) => setAssignee(e.target.value)}>
              <option value="">Unassigned</option>
              {team.filter((m) => m.active !== false).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </Select>
          </div>
          <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}/></div>
          <div><Label>Link to project (optional)</Label>
            <Select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="wo-project">
              <option value="">(Standalone)</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="wo-create-btn">{saving ? "Creating…" : "Create work order"}</Button>
        </div>
      </form>
    </div>
  );
}

function Row({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-[hsl(var(--text-muted))] mt-0.5 shrink-0" strokeWidth={1.6}/>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">{label}</div>
        <div className={`${mono ? "font-mono text-[11px]" : "text-[13px]"} truncate`}>{value || "—"}</div>
      </div>
    </div>
  );
}
